import { useTools } from '@risksmart-app/components/src/tools/useTools';
import { ParentTypes as Parent_Type_Enum } from '@risksmart-app/domain/src/types/consts/parent-type';
import type { RiskAssessmentResultControlType as Risk_Assessment_Result_Control_Type_Enum } from '@risksmart-app/domain/src/types/consts/risk-assessment-result-control-type';
import type { FC, ReactNode } from 'react';
import { ModalBodyWrapper } from 'src/components/form/form/ModalBodyWrapper';
import { PageWrapper } from 'src/components/form/form/PageWrapper';
import { useWizardStore } from 'src/components/wizard/store/useWizardStore';
import { isResultFormReadOnly } from 'src/pages/assessments/forms/resultFormReadOnly';
import RiskAssessmentResultForm from 'src/pages/assessments/forms/RiskAssessmentResultForm';
import type { RiskAssessmentResultFormDataFields } from 'src/pages/assessments/forms/riskAssessmentResultSchema';
import { defaultValues } from 'src/pages/assessments/forms/riskAssessmentResultSchema';
import type { ObjectWithContributors } from 'src/rbac/Permission';
import { useHasPermissionQuery } from 'src/rbac/useHasPermission';

import { useMultiParentFileUpdate } from '@/hooks/mutations/file/useFileUpdate';
import { useInsertChildRiskOversightResult } from '@/hooks/mutations/oversight-result/useInsertChildRiskOversightResult';
import { useUpdateControlledRiskOversightResult } from '@/hooks/mutations/oversight-result/useUpdateControlledRiskOversightResult';
import { useUpdateUncontrolledRiskOversightResult } from '@/hooks/mutations/oversight-result/useUpdateUncontrolledRiskOversightResult';
import { useGetRiskOversightResultById } from '@/hooks/queries';
import { saveFilesForOptionalCreatedEntity } from '@/utils/createWithFiles';
import { resolveRiskResultFileParentType } from '@/utils/riskResultFileParentType';
import { oversightAssessmentResultsUrl } from '@/utils/urls';

type Props = {
  readonly: boolean;
  navigateToResults: boolean;
  isModalForm: boolean;
  parentId?: string;
  assessedItem?: ObjectWithContributors;
  id?: string;
  onDismiss?: (saved: boolean) => void;
  beforeFieldsSlot?: ReactNode;
  showAssessmentSelector?: boolean;
  riskIds?: string[];
  header?: string;
};

const ConnectedRiskOversightResultForm: FC<Props> = ({
  readonly,
  parentId,
  assessedItem,
  id,
  onDismiss,
  beforeFieldsSlot,
  showAssessmentSelector,
  navigateToResults,
  isModalForm,
  riskIds,
  header,
}) => {
  const { updateFiles } = useMultiParentFileUpdate();
  const [toolsContent, _] = useTools();
  const {
    assessmentId: wizardAssessmentId,
    steps,
    currentStep,
  } = useWizardStore();

  const { data } = useGetRiskOversightResultById({
    queryArgs: { id: id! },
    shouldSkip: !id,
  });
  const controlledRiskAssessmentResult =
    data?.risk_controlled_oversight_result?.[0];
  const uncontrolledRiskAssessmentResult =
    data?.risk_uncontrolled_oversight_result?.[0];

  const riskAssessmentResult =
    controlledRiskAssessmentResult ?? uncontrolledRiskAssessmentResult;
  const parentType = controlledRiskAssessmentResult
    ? Parent_Type_Enum.RiskControlledOversightResult
    : Parent_Type_Enum.RiskUncontrolledOversightResult;
  const {
    hasPermission: canUpdateRiskAssessmentResult,
    loading: canUpdateLoading,
  } = useHasPermissionQuery(
    [
      'update:risk_controlled_oversight_result',
      'update:risk_uncontrolled_oversight_result',
    ],
    assessedItem
  );

  const { insertChildRiskOversightResult } =
    useInsertChildRiskOversightResult();

  const { updateControlledRiskOversightResult: updateRiskControlledResult } =
    useUpdateControlledRiskOversightResult();
  const {
    updateUncontrolledRiskOversightResult: updateRiskUncontrolledResult,
  } = useUpdateUncontrolledRiskOversightResult();
  riskIds = riskIds ?? [];
  const parentRisk = riskAssessmentResult?.parents.find((p) => p.risk);
  if (parentRisk?.risk?.Id) {
    riskIds.push(parentRisk.risk.Id);
  } else if (assessedItem?.Id) {
    riskIds.push(assessedItem.Id);
  }

  const onSave = async (values: RiskAssessmentResultFormDataFields) => {
    const { files } = values;
    const riskAssessmentResultIds: string[] = [];
    const oversightAssessmentId =
      parentId || values.OversightAssessmentId || undefined;
    if (!riskAssessmentResult && !oversightAssessmentId) {
      throw new Error('An oversight assessment is required');
    }
    if (riskAssessmentResult) {
      if (parentType === Parent_Type_Enum.RiskControlledOversightResult) {
        const result = await updateRiskControlledResult({
          Impact: values.Impact,
          Likelihood: values.Likelihood,
          Rating: values.Rating,
          Rationale: values.Rationale,
          TestDate: values.TestDate,
          CustomAttributeData: values.CustomAttributeData ?? null,
          Id: riskAssessmentResult.Id,
        });
        if (
          result.update_risk_controlled_oversight_result?.affected_rows === 0
        ) {
          throw new Error('Risk result update failed');
        }
      } else {
        const result = await updateRiskUncontrolledResult({
          Impact: values.Impact,
          Likelihood: values.Likelihood,
          Rating: values.Rating,
          Rationale: values.Rationale,
          TestDate: values.TestDate,
          CustomAttributeData: values.CustomAttributeData ?? null,
          Id: riskAssessmentResult.Id,
        });
        if (
          result.update_risk_uncontrolled_oversight_result?.affected_rows === 0
        ) {
          throw new Error('Risk result update failed');
        }
      }

      riskAssessmentResultIds.push(id!);
    } else {
      const result = await insertChildRiskOversightResult({
        OversightAssessmentId: oversightAssessmentId!,
        RiskIds: values?.RiskIds?.map((r) => r.value) ?? [],
        ControlType: values.ControlType,
        Rating: values.Rating,
        Likelihood: values.Likelihood,
        Impact: values.Impact,
        Rationale: values.Rationale,
        TestDate: values.TestDate,
        CustomAttributeData: values.CustomAttributeData ?? null,
      });

      if (!result.insertChildRiskOversightResult?.Ids?.[0]) {
        throw new Error('Risk assessment result id is missing');
      }
      riskAssessmentResultIds.push(
        ...result.insertChildRiskOversightResult.Ids
      );
    }

    const createdEntity = !riskAssessmentResult
      ? {
          id: riskAssessmentResultIds[0],
          destination: oversightAssessmentResultsUrl(oversightAssessmentId!),
        }
      : undefined;
    const fileParentType = resolveRiskResultFileParentType({
      existingParentType: riskAssessmentResult ? parentType : undefined,
      controlType: values.ControlType,
      controlledParentType: Parent_Type_Enum.RiskControlledOversightResult,
      uncontrolledParentType: Parent_Type_Enum.RiskUncontrolledOversightResult,
    });
    const saveFiles = () =>
      updateFiles({
        parentIds: riskAssessmentResultIds,
        parentType: fileParentType,
        selectedFiles: files,
        originalFiles: riskAssessmentResult?.files.map((f) => f.file) ?? [],
      });
    await saveFilesForOptionalCreatedEntity({ createdEntity, saveFiles });

    return {
      savedIds: riskAssessmentResultIds,
      createdEntity,
      successNavigation:
        navigateToResults && oversightAssessmentId
          ? {
              destination: oversightAssessmentResultsUrl(oversightAssessmentId),
            }
          : undefined,
    };
  };

  const value: RiskAssessmentResultFormDataFields | undefined =
    riskAssessmentResult
      ? {
          Rating: riskAssessmentResult.Rating,
          Rationale: riskAssessmentResult.Rationale,
          Likelihood: riskAssessmentResult.Likelihood,
          Impact: riskAssessmentResult.Impact,
          ControlType: controlledRiskAssessmentResult
            ? 'Controlled'
            : 'Uncontrolled',
          OversightAssessmentId: parentId,
          RiskIds: riskIds.map((c) => ({ value: c })),
          TestDate: riskAssessmentResult?.TestDate,
          CustomAttributeData: riskAssessmentResult?.CustomAttributeData,
          files: riskAssessmentResult?.files.map((f) => f.file) ?? [],
        }
      : undefined;

  return (
    <RiskAssessmentResultForm
      header={header}
      defaultValues={{
        ...defaultValues,
        OversightAssessmentId:
          toolsContent === 'wizard' ? wizardAssessmentId : parentId,
        RiskIds: riskIds.map((c) => ({ value: c })),
        ControlType:
          (steps[currentStep]
            ?.controlType as Risk_Assessment_Result_Control_Type_Enum) ||
          defaultValues.ControlType,
      }}
      values={value}
      onSave={onSave}
      readOnly={isResultFormReadOnly({
        readonly,
        permissionLoading: canUpdateLoading,
        canUpdate: canUpdateRiskAssessmentResult,
      })}
      onDismiss={onDismiss}
      renderTemplate={(renderProps) =>
        isModalForm ? (
          <ModalBodyWrapper {...renderProps} />
        ) : (
          <PageWrapper {...renderProps} />
        )
      }
      disableRiskSelector={id != undefined}
      beforeFieldsSlot={beforeFieldsSlot}
      showSelector={showAssessmentSelector ? 'oversight_assessment' : undefined}
      assessmentMode={'oversight_assessment'}
      configType={'oversight'}
    />
  );
};

export default ConnectedRiskOversightResultForm;
